import SearchIcon from '@mui/icons-material/Search'
import React, { useState } from 'react'
import { Button, InputAdornment, TextField, Tooltip } from '@mui/material'
import { GroupType, ProductType } from '../../resources/products/types'
import { GroupSelection } from './GroupSelection'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  allProductsById,
  groupsByProductId,
  productTocAtom,
} from 'shared/api/masters'
import { Table } from 'shared/ui/Table/Table'

export function ProductSelection({
  onSelect,
  selectedProductId,
}: {
  onSelect(p: { id: number }): void
  selectedProductId?: number
}) {
  const [selectedGroup, setSelectedGroup] = useState<GroupType | undefined>(
    undefined,
  )
  const [selectingProduct, setSelectingProduct] = useState(false)
  const allGroups = useAtomValue(groupsByProductId)

  const onGroupSelect = (g?: GroupType) => {
    setSelectedGroup(g)
    setSelectingProduct(true)
  }

  const group =
    selectedGroup ||
    (selectedProductId && allGroups[selectedProductId]) ||
    undefined
  if (!group || !selectingProduct)
    return (
      <GroupSelection onSelect={onGroupSelect} selectedGroupId={group?.id} />
    )

  return (
    <ProductSelect
      group={group}
      onCancel={() => setSelectingProduct(false)}
      onSelect={onSelect}
      selectedProductId={selectedProductId}
    />
  )
}

function ProductSelect({
  group,
  onCancel,
  onSelect,
  selectedProductId,
}: {
  group: GroupType
  onCancel(): void
  onSelect(p: ProductType): void
  selectedProductId?: number
}) {
  const allProducts = useAtomValue(allProductsById) ?? {}
  const { grouped } = useAtomValue(productTocAtom)
  const selectGroup = grouped[group.id] ?? []
  const [searchValue, setSearchValue] = useState('')

  const searchTerms = searchValue
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean)

  const filteredProductIds = selectGroup.filter((productId) => {
    const product = allProducts[productId]
    if (!product?.title) return false
    const normalizedTitle = product.title.toLowerCase()
    return searchTerms.every((term) => normalizedTitle.includes(term))
  })

  const columns = [
    {
      title: '',
      width: 100,
      rowContent(id: number) {
        const title = allProducts[id]?.title ?? `Unknown Product (${id})`
        if (id === selectedProductId) {
          return <strong>{title}</strong>
        }
        return (
          <Tooltip title={title} enterDelay={500} placement="right">
            <span>{title}</span>
          </Tooltip>
        )
      },
    },
  ]
  const selectedIndex = (index: number) => {
    const productId = filteredProductIds[index]
    const selectedProduct = allProducts[productId]
    if (!selectedProduct) return
    onSelect(selectedProduct)
  }
  return (
    <Table
      title={group?.name ?? 'Select Product'}
      items={filteredProductIds}
      columns={columns}
      onRowClick={selectedIndex}
      toolBarItems={[
        <Button key="cancel" onClick={onCancel}>
          Back
        </Button>,
        <TextField
          key="search"
          size="small"
          label="Search"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          inputProps={{ 'aria-label': 'Search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />,
      ]}
    />
  )
}
